import type { LanguageCode } from '@/stores/locale';

/**
 * Maps language codes to their locale identifiers used for date formatting
 */
const localeIdentifierMap: Record<LanguageCode, string> = {
  da: 'da-DK',
  en: 'en-GB',
  fi: 'fi-FI',
  fr: 'fr-FR',
  nb: 'nb-NO',
  nl: 'nl-NL',
  ru: 'ru-RU',
  sv: 'sv-SE',
  de: 'de-DE',
} as const;

/**
 * Cache for DateTimeFormat instances to avoid recreating them on every call
 * Pre-initialized at module load time for optimal performance
 */
const dateFormatters = new Map<string, Intl.DateTimeFormat>(
  Object.entries(localeIdentifierMap).map(([_, localeIdentifier]) => [
    localeIdentifier,
    new Intl.DateTimeFormat(localeIdentifier, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }),
  ]),
);

/**
 * Gets the cached DateTimeFormat instance for the given locale
 */
const getDateFormatter = (locale: LanguageCode): Intl.DateTimeFormat => {
  const localeIdentifier = localeIdentifierMap[locale];
  return dateFormatters.get(localeIdentifier)!;
};

/**
 * Detects if a timestamp is in seconds (Unix timestamp) or milliseconds
 * Unix timestamps are typically 10 digits (in seconds), while JS timestamps are 13 digits (in milliseconds)
 */
const normalizeTimestamp = (timestamp: number): number => {
  // If the timestamp is less than 10 billion (10 digits), it's likely in seconds
  // This works until year 2286, which should be sufficient
  if (timestamp < 10000000000) {
    return timestamp * 1000; // Convert to milliseconds
  }
  return timestamp;
};

/**
 * Converts a date value (Date, number timestamp, or string) to a localized date string
 *
 * @param dateValue - The date to format (Date object, Unix timestamp in seconds/milliseconds, or ISO string)
 * @param locale - The language code for localization
 * @returns Formatted date string, or empty string if invalid
 *
 * @example
 * ```ts
 * formatDate(1644595200, 'en') // "02/11/2022" (Unix timestamp in seconds)
 * formatDate(1644595200000, 'da') // "11-02-2022" (timestamp in milliseconds)
 * formatDate(new Date(), 'de') // "11.02.2022"
 * ```
 */
export const formatDate = (
  dateValue: Date | number | string | null | undefined,
  locale: LanguageCode,
): string => {
  if (dateValue == null) return '';

  try {
    let date: Date;

    if (typeof dateValue === 'number') {
      // Handle Unix timestamps (detect and normalize seconds vs milliseconds)
      const normalizedTimestamp = normalizeTimestamp(dateValue);
      date = new Date(normalizedTimestamp);
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      // Handle string dates (ISO format, etc.)
      date = new Date(dateValue);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return String(dateValue);
    }

    // Use cached DateTimeFormat instance for better performance
    const formatter = getDateFormatter(locale);
    const dateString = formatter.format(date);

    // Special case: Danish locale uses hyphens instead of dots in TimeLog
    if (locale === 'da') {
      return dateString.replace(/\./g, '-');
    }

    return dateString;
  } catch (error) {
    console.error('Error formatting date:', error);
    // Fallback to string representation if formatting fails
    return String(dateValue);
  }
};
